import React, { useEffect, useMemo, useState } from "react";
import { ordersApi, expensesApi } from "../api";
import { getTimeframe, toIsoRange } from "../timeframe";
import { useI18n } from "../i18n";
import { userApi } from "../api";
import SubscriptionBanner from "./SubscriptionBanner";
import { useAccount } from "../contexts/AccountContext";

function dateKeyLocal(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function toJsDateMaybe(tsOrDate) {
  if (tsOrDate && typeof tsOrDate === "object" && "_seconds" in tsOrDate) {
    return new Date(tsOrDate._seconds * 1000);
  }
  return new Date(tsOrDate || Date.now());
}

export default function PL({ user, subscriptionStatus, onSubscribe }) {
  const { t } = useI18n();
  const { selectedAccountId, viewMode, setViewMode } = useAccount();
  const [orders, setOrders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [range, setRange] = useState("today"); // today | week | month | year | custom
  const [loading, setLoading] = useState(true);
  const [subStatus, setSubStatus] = useState({ valid: true, plan: 'free' });
  const [subPending, setSubPending] = useState(false);
  const [subInfo, setSubInfo] = useState("");
  const todayInit = new Date();
  const yearStartInit = new Date(todayInit.getFullYear(), 0, 1);
  const yearEndInit = new Date(todayInit.getFullYear(), 11, 31);
  const [customFrom, setCustomFrom] = useState(dateKeyLocal(yearStartInit));
  const [customTo, setCustomTo] = useState(dateKeyLocal(todayInit));

  const fetchData = async () => {
    try {
      // Always fetch by timeframe from backend
      let fromParam;
      let toParam;
      if (range === 'custom') {
        if (customFrom && customTo) {
          const fromD = new Date(customFrom);
          const toD = new Date(customTo);
          toD.setHours(23, 59, 59, 999);
          fromParam = fromD.toISOString();
          toParam = toD.toISOString();
        }
      } else {
        const tf = getTimeframe();
        const iso = toIsoRange(tf);
        fromParam = iso.from;
        toParam = iso.to;
      }
      
      const params = {};
      if (fromParam && toParam) {
        params.from = fromParam;
        params.to = toParam;
      }
      if (viewMode === 'account' && selectedAccountId) {
        params.accountId = selectedAccountId;
      }
      
      const [ordersData, expensesData] = await Promise.all([
        ordersApi.list(params),
        expensesApi.list(params),
      ]);
      const mapped = (ordersData || []).map((o) => {
        const dt = toJsDateMaybe(o.date);
        dt.setHours(0, 0, 0, 0);
        return {
          date: dt,
          sellingPrice: Number(o.sellingPrice) || 0,
          totalCost: Number(o.totalCost) || 0,
          name: o.name || '',
        };
      });
      setOrders(mapped);
      const mappedExpenses = (expensesData || []).map((e) => {
        const d = toJsDateMaybe(e.date);
        d.setHours(0, 0, 0, 0);
        return {
          id: e.expenseId,
          date: d,
          amount: Number(e.amount) || 0,
          frequency: (e.frequency || 'daily').toLowerCase(),
          category: e.category || 'Other',
          note: e.note || '',
        };
      });
      setExpenses(mappedExpenses);
    } catch (e) {
      console.error("Failed to load P&L data", e);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchData();
    const onVis = () => { if (document.visibilityState === 'visible') fetchData(); };
    document.addEventListener('visibilitychange', onVis);
    const onTf = () => { if (range !== 'custom') fetchData(); };
    window.addEventListener('TP_TIMEFRAME_CHANGE', onTf);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('TP_TIMEFRAME_CHANGE', onTf); };
  }, [range, customFrom, customTo, selectedAccountId, viewMode]);

  const displayRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let from = new Date(today);
    let to = new Date(today);
    if (range === 'week') {
      const day = from.getDay();
      const offset = (day + 6) % 7; // Monday start
      from.setDate(from.getDate() - offset);
      to = new Date(from);
      to.setDate(from.getDate() + 6);
    } else if (range === 'month') {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (range === 'year') {
      from = new Date(today.getFullYear(), 0, 1);
      to = new Date(today.getFullYear(), 11, 31);
    } else if (range === 'custom') {
      const f = customFrom ? new Date(customFrom) : today;
      const t = customTo ? new Date(customTo) : today;
      return { from: dateKeyLocal(f), to: dateKeyLocal(t) };
    }
    return { from: dateKeyLocal(from), to: dateKeyLocal(to) };
  }, [range, customFrom, customTo]);

  useEffect(() => {
    const start = Date.now();
    try { if (window && window.gtag) window.gtag('event', 'engagement_begin', { screen_name: 'pl' }); } catch {}
    return () => {
      try { if (window && window.gtag) window.gtag('event', 'engagement_time', { screen_name: 'pl', value: Date.now() - start }); } catch {}
    };
  }, []);

  // Always refresh subscription status when range changes (non-cached)
  useEffect(() => {
    (async () => {
      try {
        const status = await userApi.subscriptionStatus();
        setSubStatus(status || { valid: true, plan: 'free' });
      } catch {
        setSubStatus({ valid: true, plan: 'free' });
      }
    })();
  }, [range]);

  const { revenue, cogs, gross, expensesTotal, monthlyExpense, net, filteredCount, label, perDay, days, ledger } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let from = new Date(today);
    let to = new Date(today);
    let label = t('pl.range.label.today');
    if (range === "week") {
      const day = from.getDay();
      const offset = (day + 6) % 7; // Monday as start
      from.setDate(from.getDate() - offset);
      to = new Date(from);
      to.setDate(from.getDate() + 6);
      label = t('pl.range.label.week');
    } else if (range === "month") {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      label = t('pl.range.label.month');
    } else if (range === "year") {
      from = new Date(today.getFullYear(), 0, 1);
      to = new Date(today.getFullYear(), 11, 31);
      label = t('pl.range.label.year');
    } else if (range === "custom") {
      from = new Date(customFrom || dateKeyLocal(today));
      to = new Date(customTo || dateKeyLocal(today));
      label = t('pl.range.label.custom');
    }
    const fromKey = dateKeyLocal(from);
    const toKey = dateKeyLocal(to);

    const within = (d) => {
      const k = dateKeyLocal(d);
      return k >= fromKey && k <= toKey;
    };

    const filtered = orders.filter((o) => within(o.date));
    const revenue = filtered.reduce((s, o) => s + (o.sellingPrice || 0), 0);
    const cogs = filtered.reduce((s, o) => s + (o.totalCost || 0), 0);
    const gross = revenue - cogs;

    const days = [];
    const copy = new Date(from);
    while (dateKeyLocal(copy) <= toKey) {
      days.push(dateKeyLocal(copy));
      copy.setDate(copy.getDate() + 1);
    }
    const byDay = Object.fromEntries(days.map((d) => [d, 0]));
    for (const o of filtered) {
      const key = dateKeyLocal(o.date);
      const profit = (o.sellingPrice || 0) - (o.totalCost || 0);
      byDay[key] = (byDay[key] || 0) + profit;
    }
    const perDay = days.map((d) => byDay[d] || 0);

    const dailyExpense = expenses
      .filter((e) => e.frequency === 'daily' && within(e.date))
      .reduce((s, e) => s + (e.amount || 0), 0);
    const monthlyExpenseCalc = (range === 'month' || range === 'year' || range === 'custom')
      ? expenses
          .filter((e) => e.frequency === 'monthly' && within(e.date))
          .reduce((s, e) => s + (e.amount || 0), 0)
      : 0;
    const monthlyExpense = monthlyExpenseCalc;
    const expensesTotal = dailyExpense + monthlyExpenseCalc;
    const net = gross - expensesTotal;

    // Build ledger entries
    const orderEntries = filtered.flatMap((o) => ([
      { kind: 'credit', label: o.name || t('pl.ledger.sale'), note: '', amount: o.sellingPrice || 0, date: o.date },
      { kind: 'debit', label: t('pl.ledger.cogs'), note: o.name || '', amount: o.totalCost || 0, date: o.date },
    ]));
    const expenseEntries = expenses
      .filter((e) => (e.frequency === 'daily' && within(e.date)) || ((range === 'month' || range === 'year' || range === 'custom') && e.frequency === 'monthly' && within(e.date)))
      .map((e) => ({ kind: 'debit', label: e.category || t('pl.ledger.expense'), note: (e.note || ''), amount: e.amount || 0, date: e.date }));
    const ledger = [...orderEntries, ...expenseEntries].sort((a, b) => +a.date - +b.date);

    return { revenue, cogs, gross, expensesTotal, monthlyExpense, net, filteredCount: filtered.length, label, perDay, days, ledger };
  }, [orders, range, expenses]);

  const maxBar = Math.max(1, ...perDay);

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
      {/* Header with mobile-friendly layout */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-bold">{t('pl.title')}</div>
        </div>
        
        {/* Controls - stacked on mobile, side-by-side on desktop */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* View Mode Toggle - mobile-friendly */}
          <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] text-gray-600 mb-1">View Mode</div>
                <div className="text-sm font-medium text-gray-800">
                  {viewMode === 'account' ? 'Business Account' : 'Organization'}
                </div>
              </div>
              <button
                onClick={() => {
                  const newViewMode = viewMode === 'account' ? 'org' : 'account';
                  setViewMode(newViewMode);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  viewMode === 'org' ? 'bg-green-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    viewMode === 'org' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          
          {/* Range Selector */}
          <div className="bg-white border border-green-100 rounded-2xl p-3 shadow-sm">
            <div className="text-[11px] text-gray-600 mb-1">Time Range</div>
            <select
              className="border border-green-200 rounded-lg px-3 py-1 text-sm text-green-700 bg-green-50 w-full sm:w-auto"
              value={range}
              onChange={(e) => setRange(e.target.value)}
            >
              <option value="today">{t('pl.range.option.today')}</option>
              <option value="week">{t('pl.range.option.week')}</option>
              <option value="month">{t('pl.range.option.month')}</option>
              <option value="year">{t('pl.range.option.year')}</option>
              <option value="custom">{t('pl.range.option.custom')}</option>
            </select>
          </div>
        </div>
      </div>


      <SubscriptionBanner 
        user={user} 
        subStatus={subscriptionStatus} 
        onSubscribe={onSubscribe}
        showCreateButton={false}
        quotaInfo={subscriptionStatus?.quotaInfo}
      />

      {/* Range pill */}
      <div className="mb-2">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 shadow-sm text-[11px] text-gray-700">
          <span>Range:</span>
          <span className="font-semibold">{displayRange.from} → {displayRange.to}</span>
        </div>
      </div>

      {range === 'custom' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
          <div className="text-xs font-semibold text-green-700 mb-2">{t('pl.range.custom.title')}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('pl.range.from')}</label>
              <input
                type="date"
                className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white"
                value={customFrom}
                onChange={(e) => { const v = e.target.value; setCustomFrom(v); try { if (v && customTo) window.dispatchEvent(new CustomEvent('TP_TIMEFRAME_CHANGE', { detail: { mode: 'custom', from: v, to: customTo } })); } catch {} }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">{t('pl.range.to')}</label>
              <input
                type="date"
                className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white"
                value={customTo}
                onChange={(e) => { const v = e.target.value; setCustomTo(v); try { if (customFrom && v) window.dispatchEvent(new CustomEvent('TP_TIMEFRAME_CHANGE', { detail: { mode: 'custom', from: customFrom, to: v } })); } catch {} }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-green-50 rounded-xl p-3 text-center shadow-sm border border-green-100">
          <div className="text-xs text-gray-500 mb-1">{t('pl.metric.revenue').replace('{label}', label)}</div>
          <div className="text-sm sm:text-lg lg:text-xl font-bold text-green-700 break-words overflow-hidden">
            {loading ? "…" : `₹${Number(revenue).toLocaleString('en-IN')}`}
          </div>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center shadow-sm border border-green-100">
          <div className="text-xs text-gray-500 mb-1">{t('pl.metric.cogs').replace('{label}', label)}</div>
          <div className="text-sm sm:text-lg lg:text-xl font-bold text-green-700 break-words overflow-hidden">
            {loading ? "…" : `₹${Number(cogs).toLocaleString('en-IN')}`}
          </div>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center shadow-sm border border-green-100">
          <div className="text-xs text-gray-500 mb-1">{t('pl.metric.expenses').replace('{label}', label)}</div>
          <div className="text-sm sm:text-lg lg:text-xl font-bold text-green-700 break-words overflow-hidden">
            {loading ? "…" : `₹${Number(expensesTotal).toLocaleString('en-IN')}`}
          </div>
        </div>
      </div>

      {(range === 'year' && !(subStatus?.valid && subStatus?.plan !== 'trial')) ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-4 shadow-sm text-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="text-yellow-800">
              <div className="font-semibold mb-1">{t('pl.paywall.year.title')}</div>
              <div className="text-xs">{t('pl.paywall.year.desc')}</div>
              {subInfo && <div className="text-xs text-green-700 mt-2">{subInfo}</div>}
            </div>
            <button
              disabled={subPending}
              className={`text-xs px-3 py-1.5 rounded ${subPending ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600 text-white'}`}
              onClick={async ()=>{
                try {
                  setSubPending(true);
                  const res = await userApi.activateSubscription('annual');
                  const status = await userApi.subscriptionStatus();
                  setSubStatus(status);
                  setSubInfo(res?.message || '');
                } catch { /* noop */ }
                finally { setSubPending(false); }
              }}
            >
              {subPending ? t('dash.paywall.pending') : t('dash.paywall.subscribe')}
            </button>
          </div>
        </div>
      ) : null}

      {(range !== 'year' || (subStatus?.valid && subStatus?.plan !== 'trial')) && (
      <div className="bg-white border border-green-100 rounded-2xl p-3 mb-4 shadow-sm flex items-center justify-between text-sm">
        <div className="text-gray-700 break-words">{t('pl.metric.gross')}: <span className="font-semibold text-green-700">₹{Number(gross).toLocaleString('en-IN')}</span></div>
        <div className="text-gray-700 break-words">{t('pl.metric.net')}: <span className={`font-semibold ${net < 0 ? 'text-red-700' : 'text-green-700'}`}>₹{Number(net).toLocaleString('en-IN')}</span></div>
      </div>
      )}

      {(range !== 'year' || (subStatus?.valid && subStatus?.plan !== 'trial')) && (
      <div className="bg-white border border-green-100 rounded-2xl p-4 mb-4 shadow-sm">
        <div className="text-sm font-semibold mb-2">{t('pl.section.how')}</div>
        <div className="text-xs text-gray-600">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-3 h-3 rounded-full bg-green-400 inline-block" />
            <span>{t('pl.how.revenue')}</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-3 h-3 rounded-full bg-green-300 inline-block" />
            <span>{t('pl.how.cogs')} </span>
          </div>
          {range === 'month' ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
                <span>{t('pl.how.gross')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-600 inline-block" />
                <span>{t('pl.how.netMonthly')}</span>
              </div>
            </>
          ) : range === 'year' ? (
            <>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
                <span>{t('pl.how.gross')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-600 inline-block" />
                <span>{t('pl.how.netMonthly')}</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
              <span>{t('pl.how.gross')}</span>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Ledger */}
      {(range !== 'year' || (subStatus?.valid && subStatus?.plan !== 'trial')) && (
      <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm">
        <div className="text-sm font-semibold mb-3">{t('pl.ledger.title').replace('{label}', label)}</div>
        {ledger.length === 0 ? (
          <div className="text-xs text-gray-500">{t('pl.ledger.empty')}</div>
        ) : (
          <div className="divide-y">
            {ledger.map((e, idx) => (
              <div key={idx} className="py-2 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${e.kind === 'credit' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-green-100 text-gray-600'}`}>
                    {e.kind === 'credit' ? t('pl.ledger.credit') : t('pl.ledger.debit')}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{e.label}</div>
                    <div className="text-[11px] text-gray-500 truncate">{new Date(e.date).toLocaleDateString()} {e.note ? `· ${e.note}` : ''}</div>
                  </div>
                </div>
                <div className={`text-sm font-semibold ${e.kind === 'credit' ? 'text-green-700' : 'text-gray-700'}`}>₹{Number(e.amount || 0).toFixed(0)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {(range !== 'year' || (subStatus?.valid && subStatus?.plan !== 'trial')) && (
      <div className="bg-green-50 rounded-2xl p-4 mb-4 border border-green-100">
        <div className="text-sm font-semibold mb-2">{t('pl.section.trend').replace('{label}', label)}</div>
        <PLChart values={perDay} maxVal={maxBar} />
      </div>
      )}

      <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm">
        <div className="text-sm font-semibold mb-1">{t('pl.tip.title')}</div>
        <div className="text-xs text-gray-600">{t('pl.tip.body')}</div>
      </div>
    </div>
  );
}

function PLChart({ values, maxVal }) {
  const [active, setActive] = React.useState(null);
  const [hoverIdx, setHoverIdx] = React.useState(null);
  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  return (
    <div className="relative">
      <div className="h-24 bg-white rounded-lg shadow-inner flex items-end gap-1 p-2">
        {values.map((val, idx) => {
          const pct = Math.max(5, Math.round((val / Math.max(1, maxVal)) * 100));
          const shades = ["bg-green-100","bg-green-200","bg-green-300","bg-green-400"];
          const shade = shades[Math.min(shades.length - 1, Math.floor((pct / 100) * shades.length))];
          const isActive = active === idx || hoverIdx === idx;
          return (
            <button
              key={idx}
              type="button"
              className={`flex-1 rounded-sm ${shade} relative focus:outline-none`} style={{ height: `${pct}%` }}
              onClick={() => setActive(active === idx ? null : idx)}
              onMouseEnter={() => setHoverIdx(idx)}
              onMouseLeave={() => setHoverIdx(null)}
              aria-label={fmt(val)}
              title={fmt(val)}
            >
              {isActive && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-green-700 text-white text-[10px] px-2 py-0.5 rounded shadow">
                  {fmt(val)}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


